import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Modal } from "../../components/admin/Modal";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import { ModelTabs, type ModelTabDefinition } from "../../components/bedrockModels/ModelTabs";
import { ProviderAllows, type ProviderAllowItem } from "../../components/bedrockModels/ProviderAllows";
import { TakeoverDialog } from "../../components/bedrockModels/TakeoverDialog";
import {
  allowKey,
  allowRoutes,
  allTab,
  changeCount,
  emptySelection,
  enabledTab,
  estimatedSize,
  exceptionsOf,
  groupByFamily,
  isCovered,
  isEmptySelection,
  isOn,
  modelTabId,
  modelTabPanelId,
  providerEstimate,
  providerLabel,
  providerTab,
  providersOf,
  selectionOf,
  sortedAllows,
  sortedIds,
  tabCounts,
  toggleProvider,
  toggleRow,
  toggleRows,
  toRows,
  visibleRows,
  type ModelRow,
  type Selection,
  type TabKey,
} from "../../components/bedrockModels/bedrockModelsDraft";
import styles from "../../components/bedrockModels/BedrockModels.module.css";
import {
  fetchBedrockGroup,
  fetchBedrockGroups,
  previewBedrockTakeover,
  takeOverBedrockGroup,
  updateBedrockGroup,
  type BedrockGroupDetail,
  type BedrockGroupSummary,
  type BedrockProblem,
  type BedrockResult,
  type BedrockSelection,
  type BedrockTakeoverPreview,
} from "../../api/bedrockModels";
import { Dropdown } from "../../components/shared/Dropdown";
import { useNarrow } from "../../hooks/useNarrow";

const ManagePermission = "bedrock.models.manage";

/** The problems after which the page's picture of the group is stale, so the page reads it again and drops the draft. */
const reloadAndDiscard: readonly string[] = [
  "policy-changed",
  "unknown-ids",
  "unknown-provider",
  "exception-outside-allow",
  "redundant-ids",
  "foreign-policy",
  "not-foreign",
  "no-policy",
  "unavailable",
];

/** The problems that say writes are stopped: the page reads the group again (for its banner) but keeps the person's draft. */
const reloadAndKeep: readonly string[] = ["writes-off", "kill-switch", "renderer-not-configured"];

type GroupsState = { kind: "loading" } | { kind: "loaded"; groups: BedrockGroupSummary[] } | { kind: "error"; message: string };
type ViewState = { kind: "loading" } | { kind: "loaded"; detail: BedrockGroupDetail } | { kind: "error"; message: string };
type Busy = "update" | "refresh" | "preview" | "takeover" | null;
type Dialog =
  | null
  | { kind: "empty" }
  | { kind: "discard"; action: "refresh" }
  | { kind: "discard"; action: "group"; group: string }
  | { kind: "takeover"; preview: BedrockTakeoverPreview };

/** The text of a refusal: the page's own words for its reason, else the server's, else a generic one. */
function problemText(problem: BedrockProblem, t: TFunction): string {
  const key = `bedrockModels.problems.${problem.reason}`;
  const text = t(key, {
    defaultValue: "",
    ids: problem.unknownIds.join(", "),
    providers: problem.unknownProviders.map((allow) => `${providerLabel(allow.provider)} (${allow.route})`).join(", "),
    outsideIds: problem.outsideAllowIds.join(", "),
    redundantIds: problem.redundantIds.join(", "),
    size: problem.size ?? 0,
    limit: problem.limit ?? 0,
  });
  if (text !== "") {
    return text;
  }

  return problem.message !== "" ? problem.message : t("bedrockModels.problems.generic");
}

/** The text of a call that did not succeed, for a failed load or refresh. */
function failureText(result: Exclude<BedrockResult<unknown>, { kind: "ok" }>, t: TFunction): string {
  switch (result.kind) {
    case "denied":
      return t("admin.denied.description", { permissions: ManagePermission });
    case "notFound":
      return t("bedrockModels.loadNotFound");
    case "problem":
      return result.message !== "" ? result.message : t("bedrockModels.loadFailed");
    default:
      return result.message !== "" ? result.message : t("bedrockModels.loadFailed");
  }
}

/** The body a group is sent: single ids, with provider allows and exceptions only when there are any. */
function selectionBody(selection: Selection): BedrockSelection {
  return {
    allowedIds: sortedIds(selection.ids),
    ...(selection.allows.size > 0 ? { providerAllows: sortedAllows(selection) } : {}),
    ...(selection.denied.size > 0 ? { deniedIds: sortedIds(selection.denied) } : {}),
  };
}

/** How many models the group allows in effect, as the server reports them. */
function allowedModelCount(detail: BedrockGroupDetail): number {
  if ((detail.providerAllows ?? []).length === 0) {
    return detail.allowedIds.length;
  }

  return detail.families.reduce((total, family) => total + family.models.filter((model) => model.allowed).length, 0);
}

/**
 * The "Bedrock models" screen (AIA-109 model management, phase 5): which models each Bedrock access group may invoke. The person edits a
 * local draft and sends it with Update; nothing is stored in the browser and IAM is never called from here. AWS is the only source of
 * truth, so a stale policy hash, a changed model list or a stopped writer is told by toast and the page reads the group again. A group
 * the portal did not write (a Terraform-seeded one) is replaced only through the explicit take-over confirmation. It is a tab of the
 * Providers screen, which shows it only to a person holding `bedrock.models.manage`.
 */
export function BedrockModelsContent(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const narrow = useNarrow();
  const [groupsState, setGroupsState] = useState<GroupsState>({ kind: "loading" });
  const [selected, setSelected] = useState("");
  const [view, setView] = useState<ViewState>({ kind: "loading" });
  const [draft, setDraft] = useState<Selection>(() => emptySelection());
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabKey>(allTab);
  const [busy, setBusy] = useState<Busy>(null);
  const [dialog, setDialog] = useState<Dialog>(null);

  const detail = view.kind === "loaded" ? view.detail : null;

  /** Reads a group. `fresh` shows the loading state and empties the draft; `keepDraft` and `refresh` leave the view where it is. */
  const loadGroup = useCallback(
    async (group: string, mode: "fresh" | "keepDraft" | "refresh"): Promise<BedrockResult<BedrockGroupDetail>> => {
      if (mode === "fresh") {
        setView({ kind: "loading" });
        setDraft(emptySelection());
      }

      const result = await fetchBedrockGroup(group);
      if (result.kind === "ok") {
        setView({ kind: "loaded", detail: result.response });
        if (mode !== "keepDraft") {
          setDraft(selectionOf(result.response));
        }
      } else if (mode === "fresh") {
        setView({ kind: "error", message: failureText(result, t) });
      }

      return result;
    },
    [t],
  );

  const loadGroups = useCallback(async (): Promise<void> => {
    setGroupsState({ kind: "loading" });
    const result = await fetchBedrockGroups();
    if (result.kind !== "ok") {
      setGroupsState({ kind: "error", message: failureText(result, t) });
      return;
    }

    setGroupsState({ kind: "loaded", groups: result.response });
    if (result.response.length > 0) {
      setSelected(result.response[0].name);
      await loadGroup(result.response[0].name, "fresh");
    }
  }, [loadGroup, t]);

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  const rows = useMemo<ModelRow[]>(() => (detail ? toRows(detail.families) : []), [detail]);
  const providers = useMemo(() => providersOf(rows), [rows]);
  const counts = useMemo(() => tabCounts(rows, providers, draft, search), [rows, providers, draft, search]);
  const tabs = useMemo<ModelTabDefinition[]>(
    () => [
      { key: allTab, label: t("bedrockModels.tabs.all"), count: counts[allTab] },
      { key: enabledTab, label: t("bedrockModels.tabs.enabled"), count: counts[enabledTab] },
      ...providers.map((provider) => ({ key: providerTab(provider), label: providerLabel(provider), count: counts[providerTab(provider)] })),
    ],
    [t, counts, providers],
  );
  const activeTab = tabs.some((candidate) => candidate.key === tab) ? tab : allTab;
  const shown = useMemo(() => visibleRows(rows, activeTab, draft, search), [rows, activeTab, draft, search]);
  const families = useMemo(() => groupByFamily(shown), [shown]);
  const shownAvailable = shown.filter((row) => row.model.available);
  const providerItems = useMemo<ProviderAllowItem[]>(() => {
    if (!detail) {
      return [];
    }

    const label = (route: string): string => t(`bedrockModels.route.${route}`, { defaultValue: route });
    const query = search.trim().toLowerCase();
    const forTab = (provider: string): boolean => activeTab === allTab || activeTab === enabledTab || providerTab(provider) === activeTab;
    const forSearch = (provider: string): boolean =>
      query === "" || providerLabel(provider).toLowerCase().includes(query) || shown.some((row) => row.provider === provider);
    const keys = new Set<string>();
    for (const provider of providers.filter(forTab)) {
      for (const route of allowRoutes) {
        if (rows.some((row) => row.provider === provider && row.model.route === route)) {
          keys.add(allowKey(route, provider));
        }
      }
    }

    // An allow the draft or the group has stays listed even when no model is listed for it, so it can be switched off and on again.
    for (const key of [...draft.allows, ...(detail.providerAllows ?? []).map((allow) => allowKey(allow.route, allow.provider))]) {
      keys.add(key);
    }

    return [...keys]
      .map((key) => {
        const split = key.indexOf(":");
        return { route: key.slice(0, split), provider: key.slice(split + 1) };
      })
      .filter((item) => forTab(item.provider) && forSearch(item.provider))
      .sort((a, b) => a.provider.localeCompare(b.provider) || a.route.localeCompare(b.route))
      .map((item) => {
        const saved = detail.providerAllows?.find((allow) => allow.route === item.route && allow.provider === item.provider);
        const listed = rows.some((row) => row.provider === item.provider && row.model.route === item.route);
        return {
          provider: item.provider,
          route: item.route,
          routeLabel: label(item.route),
          on: draft.allows.has(allowKey(item.route, item.provider)),
          exceptions: exceptionsOf(draft, rows, item.route, item.provider),
          hasModels: saved ? saved.hasModels : listed,
        };
      });
  }, [detail, t, providers, activeTab, rows, draft, search, shown]);

  const changes = detail ? changeCount(draft, detail) : 0;
  const dirty = changes > 0;
  const writable = detail?.writeState === "enabled";

  /** Tells a refusal by toast, then reads the group again where the page's picture of it is now stale. */
  const handleProblem = useCallback(
    async (problem: BedrockProblem, group: string): Promise<void> => {
      toast.show(problemText(problem, t), "error");
      if (reloadAndDiscard.includes(problem.reason)) {
        await loadGroup(group, "refresh");
      } else if (reloadAndKeep.includes(problem.reason)) {
        await loadGroup(group, "keepDraft");
      }
    },
    [loadGroup, t, toast],
  );

  /** Tells a failed call by toast; a refusal goes through {@link handleProblem}. */
  const handleFailure = useCallback(
    async (result: Exclude<BedrockResult<unknown>, { kind: "ok" }>, group: string): Promise<void> => {
      if (result.kind === "problem") {
        await handleProblem(result, group);
      } else {
        toast.show(failureText(result, t), "error");
      }
    },
    [handleProblem, t, toast],
  );

  const doRefresh = async (): Promise<void> => {
    setBusy("refresh");
    const result = await loadGroup(selected, "refresh");
    setBusy(null);
    if (result.kind === "ok") {
      toast.show(t("bedrockModels.refreshed", { group: selected }), "success");
    } else {
      toast.show(failureText(result, t), "error");
    }
  };

  const doSelectGroup = (group: string): void => {
    setSelected(group);
    setSearch("");
    setTab(allTab);
    void loadGroup(group, "fresh");
  };

  const requestRefresh = (): void => {
    if (dirty) {
      setDialog({ kind: "discard", action: "refresh" });
    } else {
      void doRefresh();
    }
  };

  const requestGroup = (group: string): void => {
    if (group === selected) {
      return;
    }

    if (dirty) {
      setDialog({ kind: "discard", action: "group", group });
    } else {
      doSelectGroup(group);
    }
  };

  const submitUpdate = async (confirmEmpty: boolean): Promise<void> => {
    if (!detail) {
      return;
    }

    setDialog(null);
    setBusy("update");
    const result = await updateBedrockGroup(selected, selectionBody(draft), detail.policyHash, confirmEmpty);
    if (result.kind === "ok") {
      setView({ kind: "loaded", detail: result.response });
      setDraft(selectionOf(result.response));
      toast.show(t("bedrockModels.updated", { group: selected, count: allowedModelCount(result.response) }), "success");
    } else {
      await handleFailure(result, selected);
    }

    setBusy(null);
  };

  const requestUpdate = (): void => {
    if (isEmptySelection(draft)) {
      setDialog({ kind: "empty" });
    } else {
      void submitUpdate(false);
    }
  };

  const requestTakeover = async (): Promise<void> => {
    setBusy("preview");
    const result = await previewBedrockTakeover(selected, selectionBody(draft));
    if (result.kind === "ok") {
      setDialog({ kind: "takeover", preview: result.response });
    } else {
      await handleFailure(result, selected);
    }

    setBusy(null);
  };

  const confirmTakeover = async (preview: BedrockTakeoverPreview): Promise<void> => {
    setBusy("takeover");
    const result = await takeOverBedrockGroup(selected, selectionBody(draft), preview.currentPolicyHash, isEmptySelection(draft));
    setDialog(null);
    if (result.kind === "ok") {
      setView({ kind: "loaded", detail: result.response });
      setDraft(selectionOf(result.response));
      toast.show(t("bedrockModels.takenOver", { group: selected, count: allowedModelCount(result.response) }), "success");
    } else {
      await handleFailure(result, selected);
    }

    setBusy(null);
  };

  if (groupsState.kind === "loading") {
    return <LoadingState label={t("bedrockModels.loading")} />;
  }

  if (groupsState.kind === "error") {
    return <ErrorState title={t("bedrockModels.errorTitle")} description={groupsState.message} onRetry={() => void loadGroups()} />;
  }

  if (groupsState.groups.length === 0) {
    return <EmptyState title={t("bedrockModels.emptyTitle")} description={t("bedrockModels.emptyDescription")} />;
  }

  const selectedSummary = groupsState.groups.find((group) => group.name === selected);
  const refreshDisabled = busy !== null || view.kind === "loading";
  const routeLabel = (route: string): string => t(`bedrockModels.route.${route}`, { defaultValue: route });
  const estimate = detail?.capacity ? estimatedSize(detail.capacity, rows, draft, detail) : null;
  const overLimit = detail?.capacity != null && estimate !== null && estimate > detail.capacity.limit;
  const activeLabel = tabs.find((candidate) => candidate.key === activeTab)?.label ?? "";

  return (
    <>
      <h2>{t("bedrockModels.title")}</h2>
      <p>{t("bedrockModels.intro")}</p>
      <details>
        <summary>{t("bedrockModels.help.summary")}</summary>
        <ul>
          <li>{t("bedrockModels.help.switches")}</li>
          <li>{t("bedrockModels.help.routing")}</li>
          <li>{t("bedrockModels.help.limit")}</li>
          <li>{t("bedrockModels.help.changedElsewhere")}</li>
          <li>{t("bedrockModels.help.busy")}</li>
          <li>{t("bedrockModels.help.takeover")}</li>
        </ul>
      </details>
      <div className={styles.header}>
        <div className={styles.field}>
          <label htmlFor="bedrock-group">{t("bedrockModels.group")}</label>
          <Dropdown
            id="bedrock-group"
            options={groupsState.groups.map((group) => ({ value: group.name, label: group.name }))}
            value={selected}
            disabled={busy !== null}
            onChange={requestGroup}
          />
        </div>
        <button type="button" className="button button-secondary" onClick={requestRefresh} disabled={refreshDisabled}>
          {t("bedrockModels.refresh")}
        </button>
        {detail?.replay === true && <span className={styles.replayBadge}>{t("bedrockModels.replay")}</span>}
      </div>
      {selectedSummary && selectedSummary.description !== "" && <p className={styles.description}>{selectedSummary.description}</p>}

      {view.kind === "loading" && <LoadingState label={t("bedrockModels.loadingGroup", { group: selected })} />}
      {view.kind === "error" && <ErrorState title={t("bedrockModels.errorTitle")} description={view.message} onRetry={() => void loadGroup(selected, "fresh")} />}

      {detail && (
        <>
          {detail.foreign && (
            <div className={`${styles.banner} ${styles.bannerWarning}`}>
              <p>
                <strong>{t("bedrockModels.foreign.title")}</strong> {t("bedrockModels.foreign.description")}
              </p>
              {detail.currentlyAppearsToAllow && detail.currentlyAppearsToAllow.length > 0 && (
                <details>
                  <summary>{t("bedrockModels.foreign.appears", { count: detail.currentlyAppearsToAllow.length })}</summary>
                  <p>{t("bedrockModels.foreign.appearsNote")}</p>
                  <ul className={styles.idList}>
                    {detail.currentlyAppearsToAllow.map((id) => (
                      <li key={id}>
                        <code>{id}</code>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          {detail.noPolicy && (
            <div className={styles.banner}>
              <p>{t("bedrockModels.noPolicy")}</p>
            </div>
          )}
          {!writable && (
            <div className={`${styles.banner} ${styles.bannerDanger}`}>
              <p>{t(`bedrockModels.writeState.${detail.writeState}`, { defaultValue: t("bedrockModels.writeState.unknown") })}</p>
            </div>
          )}

          <section className={styles.filters} aria-labelledby="bedrock-filters-heading">
            <h3 id="bedrock-filters-heading" className={styles.filtersHeading}>
              {t("bedrockModels.filters.title")}
            </h3>
            <div className={styles.filterRow}>
              <div className={styles.field}>
                <label htmlFor="bedrock-search">{t("bedrockModels.search")}</label>
                <input id="bedrock-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} />
              </div>
              <p className={styles.filterSummary} aria-live="polite">
                {t("bedrockModels.filters.summary", { models: shown.length, providers: providerItems.length })}
              </p>
            </div>
            <ModelTabs tabs={tabs} activeKey={activeTab} onSelect={setTab} narrow={narrow} dropdownLabel={t("bedrockModels.tabsLabel")} />
          </section>

          <div role="tabpanel" id={modelTabPanelId} tabIndex={0} {...(narrow ? { "aria-label": activeLabel } : { "aria-labelledby": modelTabId(activeTab) })}>
            <ProviderAllows
              items={providerItems}
              onToggle={(item, on) => setDraft((current) => toggleProvider(current, rows, item.route, item.provider, on))}
            />

            <section className={styles.section} aria-labelledby="bedrock-models-heading">
              <div className={styles.sectionHeader}>
                <h3 id="bedrock-models-heading" className={styles.sectionTitle}>
                  {t("bedrockModels.models.title", { count: shown.length })}
                </h3>
                <div className={styles.toolbarButtons}>
                  <button
                    type="button"
                    className="button button-secondary"
                    disabled={shownAvailable.length === 0}
                    onClick={() => setDraft((current) => toggleRows(current, shownAvailable, true))}
                  >
                    {t("bedrockModels.enableShown", { count: shownAvailable.length })}
                  </button>
                  <button
                    type="button"
                    className="button button-secondary"
                    disabled={shown.length === 0}
                    onClick={() => setDraft((current) => toggleRows(current, shown, false))}
                  >
                    {t("bedrockModels.disableShown", { count: shown.length })}
                  </button>
                </div>
              </div>
              <div className={styles.list} tabIndex={0} role="region" aria-label={t("bedrockModels.models.listLabel")}>
                {families.length === 0 ? (
                  <p className={styles.empty}>{t("bedrockModels.noMatches")}</p>
                ) : (
                  families.map((family) => (
                    <section key={family.family}>
                      <h3 className={styles.familyHeading}>
                        {family.family} ({family.rows.length})
                      </h3>
                      <ul className={styles.rows}>
                        {family.rows.map((row) => {
                          const id = row.model.id;
                          const enabled = isOn(draft, row);
                          const covered = isCovered(draft, row);
                          const excepted = covered && draft.denied.has(id);
                          return (
                            <li key={id} className={styles.row}>
                              <label className={styles.rowLabel}>
                                <input
                                  type="checkbox"
                                  role="switch"
                                  className={styles.switch}
                                  checked={enabled}
                                  disabled={covered ? !row.model.available : !row.model.available && !enabled}
                                  aria-label={t(covered ? "bedrockModels.exceptToggle" : "bedrockModels.toggle", { id })}
                                  onChange={(event) => setDraft((current) => toggleRow(current, row, event.target.checked))}
                                />
                                <span className={styles.rowText}>
                                  <span>{row.model.displayName}</span>
                                  <span className={styles.rowId}>{id}</span>
                                </span>
                              </label>
                              <span className={styles.rowTags}>
                                <span className={styles.routeTag}>{routeLabel(row.model.route)}</span>
                                {covered && !excepted && row.model.available && <span className={styles.viaTag}>{t("bedrockModels.viaProvider")}</span>}
                                {excepted && <span className={styles.exceptionTag}>{t("bedrockModels.exception")}</span>}
                                {!row.model.available && <span className={styles.unavailableTag}>{t("bedrockModels.unavailable")}</span>}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  ))
                )}
              </div>
            </section>
          </div>

          <div className={styles.stickyBar}>
            <div className={styles.stickyInfo}>
              <div className={styles.sizeBlock}>
                {detail.capacity && estimate !== null ? (
                  <>
                    <progress
                      className={overLimit ? `${styles.sizeMeter} ${styles.sizeMeterOver}` : styles.sizeMeter}
                      value={Math.min(estimate, detail.capacity.limit)}
                      max={detail.capacity.limit}
                      aria-label={t("bedrockModels.capacity.meter")}
                    />
                    <p className={overLimit ? styles.overLimit : styles.sizeText}>
                      {t("bedrockModels.capacity.estimate", { used: estimate, limit: detail.capacity.limit })}
                      {overLimit ? ` ${t("bedrockModels.capacity.over")}` : ""}
                    </p>
                  </>
                ) : (
                  <p className={styles.sizeText}>{t("bedrockModels.capacity.unavailable")}</p>
                )}
                {detail.capacity && (draft.allows.size > 0 || detail.capacity.estimatePerProvider != null || detail.capacity.estimatePerException != null) && (
                  <p className={styles.sizeHint}>
                    {t("bedrockModels.capacity.next", {
                      eu: providerEstimate(detail.capacity, "eu"),
                      global: providerEstimate(detail.capacity, "global"),
                      exception: detail.capacity.estimatePerException ?? 0,
                    })}
                  </p>
                )}
              </div>
              <div className={styles.changePills}>
                <p role="status" className={dirty ? styles.pillActive : styles.pill}>
                  {t("bedrockModels.changes", { count: changes })}
                </p>
                {(draft.allows.size > 0 || draft.denied.size > 0) && <p className={styles.pill}>{t("bedrockModels.exceptionCount", { count: draft.denied.size })}</p>}
              </div>
            </div>
            {detail.foreign ? (
              <button type="button" className="button button-primary" disabled={!writable || busy !== null} onClick={() => void requestTakeover()}>
                {t("bedrockModels.takeover.start")}
              </button>
            ) : (
              <button type="button" className="button button-primary" disabled={!dirty || !writable || busy !== null} onClick={requestUpdate}>
                {t("bedrockModels.update")}
              </button>
            )}
          </div>
        </>
      )}

      {dialog?.kind === "empty" && (
        <Modal title={t("bedrockModels.empty.title", { group: selected })} onClose={() => setDialog(null)} closeOnBackdropClick={false}>
          <p>{t("bedrockModels.empty.description", { group: selected })}</p>
          <div className={styles.dialogActions}>
            <button type="button" className="button button-secondary" onClick={() => setDialog(null)} data-autofocus>
              {t("bedrockModels.cancel")}
            </button>
            <button type="button" className="button button-primary" onClick={() => void submitUpdate(true)}>
              {t("bedrockModels.empty.confirm")}
            </button>
          </div>
        </Modal>
      )}
      {dialog?.kind === "discard" && (
        <Modal title={t("bedrockModels.discard.title")} onClose={() => setDialog(null)} closeOnBackdropClick={false}>
          <p>{t(dialog.action === "refresh" ? "bedrockModels.discard.refresh" : "bedrockModels.discard.group", { count: changes })}</p>
          <div className={styles.dialogActions}>
            <button type="button" className="button button-secondary" onClick={() => setDialog(null)} data-autofocus>
              {t("bedrockModels.discard.keep")}
            </button>
            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                const pending = dialog;
                setDialog(null);
                if (pending.action === "refresh") {
                  void doRefresh();
                } else {
                  doSelectGroup(pending.group);
                }
              }}
            >
              {t("bedrockModels.discard.confirm")}
            </button>
          </div>
        </Modal>
      )}
      {dialog?.kind === "takeover" && (
        <TakeoverDialog
          group={selected}
          preview={dialog.preview}
          selectedCount={rows.filter((row) => isOn(draft, row)).length}
          busy={busy === "takeover"}
          onConfirm={() => void confirmTakeover(dialog.preview)}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}
